import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { HttpError } from '@/shared/api/httpClient';
import { authApi } from '../api/authApi';
import logoOficina from '../../../../assets/logo.png';

type Props = {
  email: string;
  senha: string;
  nomeMaquina?: string;
  onVerificado: () => Promise<void>;
  onVoltar: () => void;
};

const INTERVALO_REENVIO_SEG = 60;

export function VerificacaoEmailPage({
  email,
  senha,
  nomeMaquina,
  onVerificado,
  onVoltar,
}: Props) {
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [segundosParaReenvio, setSegundosParaReenvio] = useState(INTERVALO_REENVIO_SEG);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (segundosParaReenvio <= 0) return;
    const id = setInterval(() => setSegundosParaReenvio((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [segundosParaReenvio]);

  async function aoSubmeter(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErro(null);
    setMensagem(null);
    if (!/^\d{6}$/.test(codigo)) {
      setErro('O código deve ter exatamente 6 dígitos.');
      return;
    }
    setCarregando(true);
    try {
      await authApi.verificar({ email, codigo, senha, nomeMaquina });
      await onVerificado();
    } catch (err) {
      if (err instanceof HttpError) {
        setErro(err.payload?.mensagem ?? 'Falha ao verificar.');
      } else {
        setErro(err instanceof Error ? err.message : 'Falha inesperada.');
      }
    } finally {
      setCarregando(false);
    }
  }

  async function reenviar() {
    if (segundosParaReenvio > 0 || carregando) return;
    setErro(null);
    setMensagem(null);
    setCarregando(true);
    try {
      await authApi.reenviarVerificacao({ email });
      setMensagem(
        'Se a conta ainda não foi verificada, enviamos um código novo. Verifique sua caixa de entrada e spam.',
      );
      setSegundosParaReenvio(INTERVALO_REENVIO_SEG);
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao reenviar.');
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#ffffff',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img
            src={logoOficina}
            alt="Oficina Ágil"
            style={{ display: 'block', width: '100%', maxWidth: 220, height: 'auto' }}
          />
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8, color: '#111827' }}>
          Confirme seu email
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
          Enviamos um código de 6 dígitos para <strong>{email}</strong>. Digite abaixo para
          ativar sua conta.
        </p>

        <form onSubmit={aoSubmeter} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label
              htmlFor="codigo"
              style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.04 }}
            >
              Código de verificação
            </label>
            <input
              id="codigo"
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={carregando}
              className="input"
              style={{
                width: '100%',
                fontSize: 24,
                textAlign: 'center',
                letterSpacing: 8,
                fontFamily: 'Courier New, monospace',
              }}
              placeholder="000000"
            />
          </div>

          {erro && (
            <div
              style={{
                padding: 12,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
              }}
            >
              {erro}
            </div>
          )}

          {mensagem && (
            <div
              style={{
                padding: 12,
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 6,
                color: '#166534',
                fontSize: 13,
              }}
            >
              {mensagem}
            </div>
          )}

          <Button
            type="submit"
            variante="primary"
            disabled={carregando || codigo.length !== 6}
          >
            {carregando ? 'Verificando...' : 'Confirmar'}
          </Button>
        </form>

        <div
          style={{
            marginTop: 20,
            paddingTop: 20,
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            fontSize: 13,
          }}
        >
          <button
            type="button"
            onClick={() => void reenviar()}
            disabled={segundosParaReenvio > 0 || carregando}
            style={{
              background: 'none',
              border: 'none',
              color: segundosParaReenvio > 0 ? '#9ca3af' : '#2563eb',
              textAlign: 'left',
              cursor: segundosParaReenvio > 0 ? 'default' : 'pointer',
              padding: 0,
              fontSize: 13,
            }}
          >
            {segundosParaReenvio > 0
              ? `Reenviar código em ${segundosParaReenvio}s`
              : 'Não recebeu? Reenviar código'}
          </button>
          <button
            type="button"
            onClick={onVoltar}
            style={{
              background: 'none',
              border: 'none',
              color: '#2563eb',
              textAlign: 'left',
              cursor: 'pointer',
              padding: 0,
              fontSize: 13,
            }}
          >
            ← Voltar para o login
          </button>
        </div>
      </div>
    </div>
  );
}
